import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DataStoreKey } from '@compiere-ws/models/compiere-data-json';
import { DetailsNoteService } from '@compiere-ws/services/details-note/details-note.service';
import { DocServerService } from '@compiere-ws/services/doc-server/doc-server.service';
import { UploadedFile } from '@iupics-components/models/uploaded-file';
import InputMentionUiComponent from '@iupics-components/standard/fields/input-mention-ui/input-mention-ui.component';
import EditTabUiComponent from '@iupics-components/standard/layouts/edit-tab-ui/edit-tab-ui.component';
import EditViewUiComponent from '@iupics-components/standard/layouts/edit-view-ui/edit-view-ui.component';
import { ModalContentType } from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import { CacheManagerService } from '@iupics-manager/managers/cache-manager/cache-manager.service';
import { SecurityManagerService } from '@iupics-manager/managers/security-manager/security-manager.service';
import { AbstractDynamicView } from '@iupics-manager/models/abstract-dynamic-view';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsEvent, IupicsTypeEvent } from '@iupics-manager/models/iupics-event';
import { UserAccount } from '@iupics-manager/models/user-account';
import { DocIconPipe } from '@iupics-util/pipes/doc-icon/doc-icon.pipe';
import { DateUtils } from '@iupics-util/tools/date.utils';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, filter, finalize, Subscription, tap } from 'rxjs';
import JoinFileUiComponent from '../../../specific/window/join-file-ui/join-file-ui.component';
import ModalUiComponent from '../../layouts/modal-ui/modal-ui.component';
import NoteUiComponent from '../note-ui/note-ui.component';
import { ChatEntry, ChatTag, NoteEntry } from '../notes.interface';
@Component({
  selector: 'iu-notes-panel-ui',
  templateUrl: './notes-panel-ui.component.html',
  styleUrls: ['./notes-panel-ui.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgClass,
    NoteUiComponent,
    FormsModule,
    InputMentionUiComponent,
    TooltipModule,
    forwardRef(() => ModalUiComponent),
    JoinFileUiComponent,
    TranslateModule,
    DocIconPipe,
  ],
})
export default class NotesPanelUiComponent
  extends AbstractDynamicView
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly #connectorService = inject(SecurityManagerService);
  readonly #detailsNoteService = inject(DetailsNoteService);
  readonly #docServerService = inject(DocServerService);
  readonly #translate = inject(TranslateService);

  @Input() table_id: number;
  @Input() currentDataStoreKey: DataStoreKey;
  @Input() parentEdit;

  @ViewChild('notesContainer') notesContainer: ElementRef<HTMLElement>;
  @ViewChild('notesWrapper') notesWrapper: ElementRef<HTMLElement>;

  subjectNote: string;
  isNotesLoaded = false;
  isNoteSending = false;

  //#region enum for template
  ModalContentType = ModalContentType;

  isFollow = false;
  chatID = -1;
  /** notes request of the record shown: a late answer of the previous record is cancelled */
  #notesSub: Subscription;
  recordID: number;
  windowID: number;
  notes: NoteEntry[];
  displayJoinFilesPanel = false;
  entryAttachment: UploadedFile = null;

  /* Users infos */
  me: UserAccount;

  /* Input note */
  @ViewChild(InputMentionUiComponent) inputMentionRef: InputMentionUiComponent;
  isTagsLoaded = false;
  tags: ChatTag[];
  tags$ = new BehaviorSubject<ChatTag[]>([]);

  ngOnInit(): void {
    this.me = this.#connectorService.getIupicsUserAccount();
    this.entryAttachment = null;
    this.loadNotesData();
    (this.parentEdit ?? this.parentTab.editViewParent).notifierLinkedComponent
      .pipe(
        filter((event: any) => event.type === IupicsTypeEvent.UPDATE_ROWSAVED),
        tap(() => this.loadNotesData()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.scrollNotesIntoView();
  }

  // (no scroll from ngAfterViewChecked: its setTimeout ran a new check of the whole app, which called it again,
  // without end when the record has no chat or the request failed)

  ngOnDestroy() {
    this.#notesSub?.unsubscribe();
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }

  onSiblingUpdate(event: IupicsEvent): void {
    this.loadNotesData();
  }

  onRemoveComponent(event: IupicsEvent): void {
    if (event.type === IupicsTypeEvent.removeChildEvent) {
      event.type = IupicsTypeEvent.removeBreadcrumbItem;
      this.DOMParentComponent.onChildUpdate(event);
      this.notifierLinkedComponent.next({
        type: IupicsTypeEvent.expandEvent,
        item: undefined,
      });
    }
  }

  addTabToEditView(editTabUi: EditTabUiComponent): void {}

  loadNotesData() {
    this.isNotesLoaded = false;
    if (this.parentEdit) {
      this.DOMParentComponent = this.parentEdit;
    }
    this.table_id = (this.DOMParentComponent as EditViewUiComponent).table_id;
    this.currentDataStoreKey = (this.DOMParentComponent as EditViewUiComponent).currentDataStoreKey;
    this.recordID = parseInt(this.currentDataStoreKey.recordId.split(',')[1]);
    this.windowID = this.currentDataStoreKey.windowId;
    if (!this.isNoteEmpty()) {
      this.clearText();
      this.entryAttachment = null;
    }
    this.getChatTags();
    this.showNotes();

    const dataStored = (this.DOMParentComponent as EditViewUiComponent).editTabs?.[0]?.dataStored?.data;
    let subject = dataStored?.['Name'];
    subject ??= dataStored?.['Value'];
    subject ??= dataStored?.['DocumentNo'];
    subject ??= `ID : ${this.currentDataStoreKey.recordId.split(',')[1]}`;
    this.subjectNote = (this.DOMParentComponent as EditViewUiComponent).data.label + ' — ' + subject;
  }

  showNotes() {
    this.notes = [];
    // no chat known for this record until its answer (0: the server finds or creates it)
    this.chatID = 0;
    this.isFollow = false;
    this.#notesSub?.unsubscribe();
    const tableID = this.table_id;
    const recordID = this.recordID;
    this.#notesSub = this.#detailsNoteService.getChatEntry(tableID, recordID).subscribe({
      next: (chat) => {
        if (tableID !== this.table_id || recordID !== this.recordID) return;
        if (chat) {
          this.isFollow = chat.follow;
          this.chatID = chat.chatID;
          this.notes = chat?.chatEntries?.map((chatEntry) => this.parseNoteData(chatEntry)) ?? [];
          this.#refreshLayout();
        }
        // loaded, chat or not: the latest note in view once drawn
        this.isNotesLoaded = true;
        this.scrollToEnd();
      },
      error: () => (this.isNotesLoaded = true),
    });
  }

  getChatTags() {
    if (CacheManagerService.chatTags[this.windowID]) {
      this.tags = CacheManagerService.chatTags[this.windowID];
      this.tags$.next(CacheManagerService.chatTags[this.windowID]);
      this.isTagsLoaded = true;
    } else {
      this.subscriptions.push(
        this.#detailsNoteService.getChatTags(this.windowID).subscribe({
          next: (tags) => {
            CacheManagerService.chatTags[this.windowID] = tags;
            this.tags = tags;
            this.tags$.next(tags);
            this.isTagsLoaded = true;
          },
        })
      );
    }
  }

  sendNote() {
    if (this.isNoteSending || this.isNoteEmpty()) {
      return;
    }

    const creatAt = new Date().toUTCString();
    const chatEntryToSend: ChatEntry = {
      chatEntryID: this.chatID,
      //created : creatAt.substring(0, 26) + creatAt.substring(27, creatAt.length),
      characterData: this.getText(),
      taggedUsers: this.getMentions(),
      subject: this.subjectNote,
      chatID: this.chatID,
      tableID: this.table_id,
      recordID: this.recordID,
      windowID: this.windowID,
      userID: this.me.id,
      userName: this.me.avatarInfos.fullname, // retrieve ad user fullname
      chatEntryAttachments: [],
    };

    if (this.entryAttachment != null) {
      chatEntryToSend.chatEntryAttachments = [
        {
          downloadURL: `${this.entryAttachment.src}`,
          fileName: `${this.entryAttachment.name}`,
          chatEntryID: 0,
          cm_ChatEntryAttachmentID: 0,
        },
      ];
    }

    this.isNoteSending = true;
    // the record shown when sending: the answer is dropped if another record is shown meanwhile
    const recordID = this.recordID;
    this.#detailsNoteService
      .sendChatEntry(chatEntryToSend)
      .pipe(finalize(() => (this.isNoteSending = false))) // an error left the panel unable to send
      .subscribe((chatEntry) => {
        if (chatEntry && recordID === this.recordID) {
          this.entryAttachment = null;
          // the chat created by the first note (it stayed 0: the next notes of the record created no new chat
          // only because the server looked it up again)
          if (chatEntry.chatID > 0) {
            this.chatID = chatEntry.chatID;
          }

          // server time (same as the list), the local one when the server did not send it
          chatEntry.created = chatEntry.created ?? creatAt;

          this.clearText();
          this.notes = [...(this.notes ?? []), this.parseNoteData(chatEntry)];
          this.#refreshLayout();
          this.scrollToEnd();
        }
      });
  }

  parseNoteData(chatEntry: ChatEntry): NoteEntry {
    const created = chatEntry?.created ?? new Date();
    return {
      id: chatEntry?.chatEntryID,
      created,
      createdAt: DateUtils.formatLongLocaleStr(created),
      time: DateUtils.simpleFormatStr(created, 'LT'),
      fullDate: DateUtils.simpleFormatStr(created, 'LLLL'),
      day: DateUtils.simpleFormatStr(created, 'YYYY-MM-DD'),
      author: {
        id: chatEntry.userID,
        fullname: chatEntry.userName,
        // pictureURL:
      },
      content: chatEntry.characterData,
      mentions: chatEntry.taggedUsers,
      attachements: (chatEntry.chatEntryAttachments ?? []).map((attachment) => {
        return {
          src: attachment.downloadURL,
          filename: attachment.fileName,
          type: attachment?.fileName?.split('.')?.[attachment.fileName.split('.').length - 1] ?? undefined,
        };
      }),
    };
  }

  /** Day separators and grouped notes (same author, same day, within 5 minutes), computed once per change */
  #refreshLayout(): void {
    const notes = this.notes ?? [];
    for (let i = 0; i < notes.length; i++) {
      const note = notes[i];
      const previous = i > 0 ? notes[i - 1] : undefined;
      const newDay = !previous || previous.day !== note.day;
      note.dayLabel = newDay ? this.#dayLabel(note.created) : undefined;
      note.continued =
        !newDay &&
        previous?.author?.id === note.author?.id &&
        Math.abs(DateUtils.diff(note.created, previous.created)) < 5 * 60 * 1000;
    }
  }

  #dayLabel(created: NoteEntry['created']): string {
    const now = new Date();
    if (DateUtils.isSame(created, now, 'day')) {
      return this.#translate.instant('detailsNote.today');
    }
    if (DateUtils.isSame(created, new Date(now.getTime() - 24 * 60 * 60 * 1000), 'day')) {
      return this.#translate.instant('detailsNote.yesterday');
    }
    return DateUtils.simpleFormatStr(created, DateUtils.isSame(created, now, 'year') ? 'dddd D MMMM' : 'dddd D MMMM YYYY');
  }

  trackNote(index: number, note: NoteEntry): number | NoteEntry {
    return note?.id || note;
  }

  follow(action) {
    if (!action) {
      this.#detailsNoteService.deleteFollowChat(this.chatID, this.me.id).subscribe((result) => {
        if (result) {
          this.isFollow = action;
        }
      });
    } else {
      this.#detailsNoteService.followChat(this.chatID, this.me.id, this.table_id, this.recordID).subscribe((result) => {
        if (result) {
          this.chatID = result.chatID;
          this.isFollow = action;
        }
      });
    }
  }

  fileUpload(event) {
    this.entryAttachment = event;
  }

  deleteEntryAttachment() {
    this.subscriptions.push(
      this.#docServerService.deleteDocument(this.entryAttachment, false).subscribe((res) => {
        this.entryAttachment = null;
      })
    );
  }

  downloadEntryAttachment(attachment) {
    const sub = this.#docServerService.downloadDocument(attachment.src as string).subscribe((response) => {
      Global.downloadFile(response, attachment.filename as string);
      sub.unsubscribe();
    });
  }

  isNoteEmpty() {
    const text = this.getText();
    return (text === null || text === undefined || text.trim().length === 0) && this.entryAttachment === null;
  }

  getText() {
    if (this.inputMentionRef) {
      return this.inputMentionRef.getContent();
    }

    return null;
  }

  getMentions() {
    return this.inputMentionRef
      .getMentions()
      .map((m) => m.value)
      .join(',');
  }

  clearText() {
    this.inputMentionRef.setContent('');
  }

  scrollNotesIntoView() {
    if (this?.parentEdit?.scrollTo) {
      setTimeout(() => {
        this.parentEdit.scrollTo();
      }, 220);
    }
  }

  scrollToEnd() {
    setTimeout(() => {
      if (this.notesWrapper?.nativeElement) {
        this.notesWrapper.nativeElement.scrollTop = this.notesWrapper.nativeElement.scrollHeight;
      }
    }, 0);
  }
}
